import { useEffect, useState, useRef } from "react";
import client, { apiError, API } from "../api/client";
import { toast } from "sonner";
import SchoolEditModal from "../components/common/SchoolEditModal";
import Pagination from "../components/common/Pagination";
import { Plus, Search, Pencil, Trash2, Download, Upload, MapPin } from "lucide-react";

export default function Schools() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null); // {school} | {} for new
  const [geo, setGeo] = useState({ total: 0, pending: 0 });
  const [geoLoading, setGeoLoading] = useState(false);
  const fileRef = useRef(null);
  const pageSize = 50;

  const load = () => {
    client.get("/schools", { params: { q: query, page, page_size: pageSize } }).then((res) => {
      setItems(res.data.items);
      setTotal(res.data.total);
    });
    client.get("/schools/meta/geocode-status").then((res) => setGeo(res.data));
  };

  useEffect(load, [query, page]);

  const doSearch = () => { setPage(1); setQuery(searchInput.trim()); };

  const del = async (s) => {
    if (!window.confirm(`Smazat školu ${s.name}?`)) return;
    try { await client.delete(`/schools/${s.id}`); toast.success("Škola smazána"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const runGeocode = async () => {
    setGeoLoading(true);
    try {
      const res = await client.post("/schools/geocode");
      toast.success(`Geokódováno ${res.data.geocoded} z ${res.data.processed} škol`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setGeoLoading(false); }
  };

  const exportSchools = async () => {
    const res = await client.get("/schools/export/all");
    const blob = new Blob([JSON.stringify({ schools: res.data }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "skoly-export.json"; a.click();
    URL.revokeObjectURL(url);
  };

  const importSchools = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const res = await client.post("/schools/import", { schools: data.schools || data });
      toast.success(`Import: ${res.data.created} nových, ${res.data.updated} aktualizováno`);
      load();
    } catch (err) { toast.error("Neplatný soubor nebo chyba importu"); }
    finally { e.target.value = ""; }
  };

  return (
    <>
      <div className="h-16 border-b border-border px-8 flex items-center justify-between sticky top-0 bg-background/80 backdrop-blur-md z-20">
        <h1 className="font-heading text-xl font-semibold tracking-tight">Školy</h1>
        <div className="flex items-center gap-2">
          {geo.pending > 0 && (
            <button data-testid="geocode-btn" onClick={runGeocode} disabled={geoLoading} className="h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary flex items-center gap-1.5 disabled:opacity-60">
              <MapPin className="h-4 w-4" /> Geokódovat adresy ({geo.pending})
            </button>
          )}
          <button data-testid="export-btn" onClick={exportSchools} className="h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary flex items-center gap-1.5">
            <Download className="h-4 w-4" /> Export
          </button>
          <button data-testid="import-btn" onClick={() => fileRef.current?.click()} className="h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary flex items-center gap-1.5">
            <Upload className="h-4 w-4" /> Import
          </button>
          <input ref={fileRef} type="file" accept=".json" onChange={importSchools} className="hidden" />
          <button data-testid="new-school-page-btn" onClick={() => setModal({})} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> Nová škola
          </button>
        </div>
      </div>

      <div className="p-8">
        <div className="flex gap-2 mb-4 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="school-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Název, město nebo IČO…"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20"
            />
          </div>
          <button data-testid="school-search-btn" onClick={doSearch} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
            Hledat
          </button>
        </div>

        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="schools-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
                <th className="text-left font-medium px-3 py-2">Název</th>
                <th className="text-left font-medium px-3 py-2">IČO</th>
                <th className="text-left font-medium px-3 py-2">Město</th>
                <th className="text-left font-medium px-3 py-2">Kontakt</th>
                <th className="text-left font-medium px-3 py-2">E-mail</th>
                <th className="text-right font-medium px-3 py-2">Akce</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-border/60 hover:bg-secondary/40" data-testid={`school-row-${s.id}`}>
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{s.ico}</td>
                  <td className="px-3 py-2">{s.city}</td>
                  <td className="px-3 py-2 text-muted-foreground">{s.contact_person}</td>
                  <td className="px-3 py-2 text-muted-foreground">{s.email}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`school-edit-${s.id}`} onClick={() => setModal({ school: s })} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`school-delete-${s.id}`} onClick={() => del(s)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">Žádné školy</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
      </div>

      {modal && (
        <SchoolEditModal
          school={modal.school}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}
